// Turns a seller profile's ICP (as mapped by the ICP preview: title, level, department, industry,
// employeeCount, revenue, location, industryExclude) into List Builder filter state, so "Build ICP
// search" opens List Builder pre-filled — the user refines it there and saves it as the agent's ICP.

const LEVEL_IDS = [
  ["founder", /found/], ["owner", /owner/], ["partner", /partner/],
  ["c_suite", /c[\s_-]?suite|c[\s-]?level|chief|cxo|\bceo\b|\bcto\b|\bcfo\b|\bcoo\b|\bcmo\b|\bcro\b/],
  ["vp", /\bvp\b|vice president/], ["director", /director/], ["head", /\bhead\b/], ["manager", /manager/],
  ["senior", /senior/], ["mid-level", /mid/], ["entry", /entry|junior/], ["intern", /intern/]
];

const arr = v => (Array.isArray(v) ? v : v ? [v] : []).map(x => String(x).trim()).filter(Boolean);
const uniq = list => [...new Set(list)];

const levelIds = values => uniq(arr(values).flatMap(v => {
  const t = v.toLowerCase();
  const exact = LEVEL_IDS.find(([id]) => id === t);
  if (exact) return [exact[0]];
  return LEVEL_IDS.filter(([, re]) => re.test(t)).map(([id]) => id);
}));

// Department names → job function ids (an id, a function label, or a whole department), using the
// backend Job Function catalog ({ departments, legacyAliases }). Retired ids map to their current ids.
const departmentIds = (values, catalog) => {
  const departments = (catalog && Array.isArray(catalog.departments)) ? catalog.departments : [];
  const legacyAliases = (catalog && catalog.legacyAliases) || {};
  return uniq(arr(values).flatMap(v => {
    const t = v.toLowerCase();
    if (Array.isArray(legacyAliases[t])) return legacyAliases[t];
    const ids = [];
    departments.forEach(dep => {
      const functions = dep.functions || [];
      if (String(dep.id).toLowerCase() === t || String(dep.label).toLowerCase().includes(t)) {
        ids.push(...functions.map(f => f.id));
        return;
      }
      functions.forEach(f => {
        if (f.id === t || f.label.toLowerCase() === t || f.label.toLowerCase().includes(t)) ids.push(f.id);
      });
    });
    return ids;
  }));
};

// "51-100" → "51 - 100" (List Builder's employee buckets have spaces).
const employeeBuckets = values => uniq(arr(values).map(v => v.replace(/\s*-\s*/, " - ")));

const titleCase = s => s.replace(/\b\w/g, c => c.toUpperCase());

export const icpToListBuilderImport = (icpFilters, departmentsCatalog) => {
  const f = icpFilters || {};
  const departments = departmentIds(f.department, departmentsCatalog);
  const filters = {};
  const selected = {};
  if (arr(f.title).length) filters.title = arr(f.title);
  if (levelIds(f.level).length) filters.level = levelIds(f.level);
  if (departments.length) filters.department = departments;
  if (arr(f.industry).length) {
    filters.industry = arr(f.industry);
    selected.industry = arr(f.industry).map(name => ({ id: name.toLowerCase(), name, checked: true }));
  }
  if (arr(f.industryExclude).length) {
    filters.industryExclude = arr(f.industryExclude);
    selected.industryExclude = arr(f.industryExclude).map(name => ({ id: name.toLowerCase(), name, checked: true }));
  }
  if (employeeBuckets(f.employeeCount).length) filters.employeeCount = employeeBuckets(f.employeeCount);
  if (arr(f.revenue).length) filters.revenue = arr(f.revenue);
  if (arr(f.location).length) {
    // ICP locations are usually countries; List Builder filters locations by name, per group.
    filters.country = arr(f.location).map(l => l.toLowerCase());
    selected.location = arr(f.location).map(l => ({
      id: `country:${l.toLowerCase()}`, name: titleCase(l), type: "Country", group: "country",
      searchText: [l.toLowerCase()], checked: true
    }));
  }
  return { filters, selected };
};

// Same encoding List Builder's ?sgLb= import decodes: base64url of UTF-8 JSON.
export const encodeListBuilderImport = payload => {
  const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// A search drafted from an agent description is stored in List Builder's saved-search shape (filters
// plus location chips); split it into the filters and chips the ?sgLb= import takes.
const CHIP_KEYS = ["locations", "locationIds", "companyLocations", "companyLocationIds"];

export const savedSearchStateToListBuilderImport = (state, companies = false) => {
  const s = state || {};
  const filters = {};
  Object.keys(s).forEach(k => { if (!CHIP_KEYS.includes(k)) filters[k] = s[k]; });
  const chips = list => (Array.isArray(list) ? list : []).map(c => ({ ...c, checked: true }));
  const names = list => arr(list).map(name => ({ id: name.toLowerCase(), name, checked: true }));
  const selected = {};
  if (s.locations) selected.location = chips(s.locations);
  if (s.companyLocations) selected.companyLocation = chips(s.companyLocations);
  if (s.industry) selected.industry = names(s.industry);
  if (s.industryExclude) selected.industryExclude = names(s.industryExclude);
  if (s.technologies) selected.technologies = arr(s.technologies).map(id => ({ id, name: id, checked: true }));
  return { filters, selected, ...(companies ? { tab: "companies" } : {}) };
};
